import { ENDPOINT } from '~/constants';

import { resolveMutationErrorMessage } from '~/data/utils';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { zodParsingCaseActionResponseSchema } from '../validations';

import { type RetryParseParsingCasePayload } from './types';

const ERROR_MESSAGE_BY_STATUS: Record<number, () => string> = {
  404: () =>
    i18n.t(($) => $.retryParseParsingCase.error404, {
      ns: 'data',
    }),
  409: () =>
    i18n.t(($) => $.retryParseParsingCase.error409, {
      ns: 'data',
    }),
  422: () =>
    i18n.t(($) => $.retryParseParsingCase.error422, {
      ns: 'data',
    }),
};

/**
 * Retry the parse step of a parsing case from the source RAW blob.
 * When `body.ocrJson` is provided, the parser uses it directly and skips OCR;
 * otherwise the full OCR pipeline re-runs.
 *
 * @param payload - The case id and optional body overrides
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_parse_vadmin_parsing_cases__case_id__actions_retry_parse_post
 */
export const retryParseParsingCase = async ({
  body,
  caseId,
}: RetryParseParsingCasePayload): Promise<ParsingCaseActionMutationResponse> => {
  try {
    return await vestigasApi
      .post(ENDPOINT.PARSING_CASE.RETRY_PARSE(caseId), {
        json: body ?? {},
        validate: { response: zodParsingCaseActionResponseSchema },
      })
      .json<ParsingCaseActionMutationResponse>();
  } catch (error) {
    log.error({
      message: 'Error retrying parse for parsing case',
      error,
    });

    const errorMessage = resolveMutationErrorMessage(
      error,
      ERROR_MESSAGE_BY_STATUS,
      () =>
        i18n.t(($) => $.retryParseParsingCase.error.generic, {
          ns: 'data',
        }),
    );
    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
